import { useQuery } from "@apollo/client/react";
import { get, isEmpty } from "lodash";
import { useMemo, useRef } from "react";

import { getGroupsByTypeQuery } from "src/queries/group.graphql";
import { throwIfRequired } from "src/util/error";
import { getRootGroupTypeIds } from "src/util/groupType";

const useRootGroupsQuery = ({
  workspace,
  featureFlags,
  isSelected,
  additionalVariables = {},
}) => {
  const savedDataRef = useRef(null);

  const rootGroupTypeIds = useMemo(
    () => getRootGroupTypeIds(get(workspace, "config.groupTypes")),
    [workspace]
  );

  const variables = {
    groupTypes: rootGroupTypeIds,
    includeEmptyGroups: true,
  };

  const { data, error, loading } = useQuery(getGroupsByTypeQuery, {
    variables: {
      ...variables,
      ...additionalVariables,
    },
    // nextFetchPolicy: "standby" was removed: in Apollo v4 it prevents the query
    // from firing when skip transitions true→false. The savedDataRef below already
    // guards against tree rebuilds triggered by cache-reference churn.
    skip: isEmpty(rootGroupTypeIds) || !isSelected || featureFlags.loading,
    errorPolicy: "all",
  });

  throwIfRequired(error);

  // This is needed as useQuery has been observed returning new objects even though
  // the cached data is unchanged, and that can trigger the GroupsNavigation component
  // to collapse and rebuild the org hierarchy tree, which can result in displaying
  // groups in pre-mutation locations, possibly as duplicates, etc.
  if (data && !savedDataRef.current) {
    savedDataRef.current = data;
  }

  const hasCachedTreeData = Boolean(savedDataRef.current);

  return {
    data: savedDataRef.current,
    error,
    // Once we have any snapshot for the tree, keep showing it instead of a
    // perpetual loader when Apollo still reports loading (e.g. Storybook sync
    // mocks + cache-and-network / background status).
    loading: loading && !hasCachedTreeData,
    rootGroupTypeIds,
  };
};

export default useRootGroupsQuery;
